import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { FC, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { PRACTICE_MODE_DURATIONS, WRITE_LOCATION_MONITORING_SCOPE } from '../../../../constants'
import ActivePracticeModeIcon from '../../../../assets/images/active-practice-mode-icon.svg?react'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { useLocation, useTogglePracticeMode } from '../../../../api/apiQueries'
import { useParams } from 'react-router-dom'
import { useAuthenticationContext } from '../../../../auth/AuthenticationContextProvider'

type PracticeModeDurationForm = {
  practiceModeDuration: number | ''
}

export const PracticeModeTab: FC = () => {
  const { toast } = useSnackbar()

  const authContext = useAuthenticationContext()
  const userHasWriteMonitoringScope = authContext.has(WRITE_LOCATION_MONITORING_SCOPE)

  const { sid: locationId } = useParams()
  const location = useLocation(locationId)

  // Get the current practice mode data from location
  const practiceModeData = location.data?.monitoring?.serviceStatus?.practiceMode
  const isPracticeModeEnabled = practiceModeData?.isEnabled ?? false
  const currentPracticeModeEndDate = practiceModeData?.endTimestamp ?? null

  // Practice mode is considered active only if both enabled and has an end date
  const isPracticeModeActive = isPracticeModeEnabled && currentPracticeModeEndDate !== null

  const formRef = useRef<HTMLFormElement>(null)
  const togglePracticeMutation = useTogglePracticeMode()

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
    reset,
    getValues
  } = useForm<PracticeModeDurationForm>({ values: { practiceModeDuration: '' } })

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  // Track practice mode state for local updates (like when ending practice mode)
  // Start with null and let it be overridden by the derived active state
  const [localPracticeModeOverride, setLocalPracticeModeOverride] = useState<string | null>(null)

  // Derive the actual active practice mode duration from backend data or local override
  const activePracticeModeDuration = useMemo(() => {
    // If we have a local override (like when ending practice mode), use that
    if (localPracticeModeOverride) {
      return localPracticeModeOverride
    }
    // Otherwise, use backend data only if practice mode is actually active
    return isPracticeModeActive ? currentPracticeModeEndDate : null
  }, [localPracticeModeOverride, isPracticeModeActive, currentPracticeModeEndDate])

  // Update local state when location data changes
  // useEffect(() => {
  //   setActivePracticeModeDuration(currentPracticeModeEndDate)
  // }, [currentPracticeModeEndDate])

  const onSubmit = async () => {
    setIsConfirmationDialogOpen(true)
  }

  const handleConfirmPracticeModeDuration = async (activePracticeModeDuration: string | null) => {
    // Stub-ish for now
    if (!locationId) return

    try {
      // If practice mode already active, end it.
      if (activePracticeModeDuration) {
        // End practice mode
        await togglePracticeMutation.mutateAsync({
          locationId: locationId,
          practiceModeEnabled: false,
          practiceModeEndDate: undefined
        })
        toast('Practice mode ended.', { variant: 'success' })
        setLocalPracticeModeOverride(null)
        // Reset the form to show "Select Duration" placeholder
        reset()
      } else {
        // Otherwise, start practice mode with selected duration
        const selectedDurationMillis = getValues('practiceModeDuration')
        const endTimestamp = calculateEndTimestamp(selectedDurationMillis)

        await togglePracticeMutation.mutateAsync({
          locationId: locationId,
          practiceModeEnabled: true,
          practiceModeEndDate: endTimestamp
        })
        setLocalPracticeModeOverride(endTimestamp)
        toast('Practice mode started.', { variant: 'success' })
      }
      setIsConfirmationDialogOpen(false)
    } catch {
      // Generate toast for error

      // Reset active practice mode duration and form value
      setLocalPracticeModeOverride(null)
      setValue('practiceModeDuration', '')
      setIsConfirmationDialogOpen(false)
      // Ensure that the global toast error works for failure case here once api is set up.
      // If it doesn't, set a toast here manually.
    }
  }

  const calculateEndTimestamp = (durationMillis: number | ''): string => {
    const now = new Date()
    const safeDurationMillis = durationMillis === '' ? 0 : durationMillis
    const endTime = new Date(now.getTime() + safeDurationMillis)

    return endTime.toISOString()
  }

  const calculateDatePracticeModeEnds = (input: string): string => {
    // Check if the input is already a timestamp (ISO format)
    const timestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
    if (timestampRegex.test(input)) {
      // It's already a timestamp, just format it
      const endTime = new Date(input)
      return endTime.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }

    const now = new Date()
    const endTime = new Date(now)

    // Extract number and unit from duration string
    const match = input.match(/(\d+)\s*(hour|hours|minute|minutes)/)
    if (match) {
      const amount = parseInt(match[1])
      const unit = match[2]

      if (unit.includes('hour')) {
        endTime.setHours(endTime.getHours() + amount)
      } else if (unit.includes('minute')) {
        endTime.setMinutes(endTime.getMinutes() + amount)
      }
    }

    return endTime.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  return (
    <Stack>
      <Stack sx={{ position: 'relative' }}>
        <Stack spacing={'var(--spacing-l)'} sx={{ width: '100%' }}>
          <Box
            sx={{
              bgcolor: 'var(--bg-surface)',
              borderRadius: 'var(--border-radius-l)',
              p: 'var(--spacing-m)',
              gap: 'var(--spacing-2xl)'
            }}
          >
            <Stack>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 'var(--spacing-s)', alignItems: 'center' }}>
                  <Typography variant="h2" fontSize={'var(--size-xl)'} fontWeight="500" color="text.primary">
                    Practice Mode
                  </Typography>
                  {activePracticeModeDuration && (
                    <Chip
                      label={'Active'}
                      data-testid={'active-practice-mode-chip'}
                      icon={<ActivePracticeModeIcon />}
                      sx={{
                        backgroundColor: 'var(--shape-confirm)',
                        color: 'var(--bg-surface)',
                        fontWeight: 400
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-m)' }}>
                  {activePracticeModeDuration ? (
                    <Button
                      disabled={!isValid || !userHasWriteMonitoringScope}
                      variant="contained"
                      color="primary"
                      onClick={() => setIsConfirmationDialogOpen(true)}
                      data-testid={'practice-mode-end-button'}
                    >
                      End Practice Mode
                    </Button>
                  ) : (
                    <Button
                      disabled={!isValid || !userHasWriteMonitoringScope}
                      variant="contained"
                      color="primary"
                      onClick={() => formRef.current?.requestSubmit()}
                      data-testid={'practice-mode-start-button'}
                    >
                      Start Practice Mode
                    </Button>
                  )}
                </Box>
              </Box>
              <Stack sx={{ marginTop: 'var(--spacing-2xs)' }}>
                <Typography sx={{ width: '22.563rem' }} variant="body1" fontWeight="400" color="text.primary">
                  Allow a customer to trigger alarms without dispatching emergency services.
                </Typography>
                <Typography
                  id="practice-mode-duration-label"
                  variant="body1"
                  sx={{ marginTop: 'var(--spacing-m)' }}
                  fontWeight="500"
                  color="text.primary"
                >
                  {activePracticeModeDuration ? 'Practice mode ends' : 'Practice Mode Duration'}
                </Typography>
                {activePracticeModeDuration ? (
                  <Typography variant="body1" sx={{ marginTop: '0.25rem' }} fontWeight="400" color="text.primary">
                    {calculateDatePracticeModeEnds(activePracticeModeDuration)}
                  </Typography>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                    <Stack sx={{ marginTop: '0.5rem' }} spacing={'var(--spacing-l)'}>
                      <Controller
                        name="practiceModeDuration"
                        control={control}
                        rules={{ required: 'A duration is required' }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            sx={{ width: '25.5rem' }}
                            slotProps={{
                              select: {
                                displayEmpty: true,
                                // @ts-expect-error - select doesn't expose the data attributes but this is valid
                                ['data-testid']: 'practice-mode-duration-dropdown'
                              }
                            }}
                            select
                            aria-labelledby="practice-mode-duration-label"
                            error={!!error}
                            helperText={error?.message}
                          >
                            {PRACTICE_MODE_DURATIONS.map(({ millis, label }) => {
                              return (
                                <MenuItem key={label} data-testid="practice-mode-duration-dropdown_value" value={millis}>
                                  {label}
                                </MenuItem>
                              )
                            })}
                          </TextField>
                        )}
                      />
                    </Stack>
                  </form>
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Stack>
      <Dialog open={isConfirmationDialogOpen} onClose={() => setIsConfirmationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{activePracticeModeDuration ? 'End Practice Mode' : 'Start Practice Mode'}</DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: 'var(--size-xs)',
              fontWeight: '300',
              lineHeight: 'var(--line-height-s, 1.125rem)',
              letterSpacing: 'var(--letter-spacing-none, 0rem)'
            }}
          >
            Professional monitoring and all related features are disabled during practice mode.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 'var(--size-2xs)' }}>
          <Button variant="outlined" onClick={() => setIsConfirmationDialogOpen(false)} data-testid="back-to-practice-mode-selection">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleConfirmPracticeModeDuration(activePracticeModeDuration)}
            data-testid="practice-mode-confirm-button"
            loading={togglePracticeMutation.isPending}
          >
            {activePracticeModeDuration ? 'End Practice Mode' : 'Start Practice Mode'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
